import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from './ui/dialog';
import { ChevronLeft, ChevronRight, Star, Plus, Trash2, Quote, ImagePlus, FileDown, Mail, Pencil, ImageDown, RotateCw, Crop as CropIcon, ExternalLink } from 'lucide-react';
import Cropper from 'react-easy-crop';
import { Slider } from './ui/slider';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GALLERY_CATEGORIES = [
  'At the jobsite',
  'In the community',
  'Microenterprise',
  'Project LIFE Gives Back',
  'Great Stories & Photos!',
];

const mediaSrc = (url) => (!url ? url : (url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`));

const linkHref = (u) => (/^https?:\/\//i.test(u) ? u : `https://${u}`);

const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => resolve(img);
  img.onerror = reject;
  img.src = src;
});

// Renders the crop + rotation to a JPEG blob (max 2000px on the long edge)
async function getEditedBlob(imageSrc, cropPx, rotation) {
  const image = await loadImage(imageSrc);
  const rad = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(rad));
  const cos = Math.abs(Math.cos(rad));
  const bW = image.width * cos + image.height * sin;
  const bH = image.width * sin + image.height * cos;
  const stage = document.createElement('canvas');
  stage.width = bW; stage.height = bH;
  const sctx = stage.getContext('2d');
  sctx.translate(bW / 2, bH / 2);
  sctx.rotate(rad);
  sctx.drawImage(image, -image.width / 2, -image.height / 2);
  const scale = Math.min(1, 2000 / Math.max(cropPx.width, cropPx.height));
  const out = document.createElement('canvas');
  out.width = Math.round(cropPx.width * scale);
  out.height = Math.round(cropPx.height * scale);
  out.getContext('2d').drawImage(
    stage, cropPx.x, cropPx.y, cropPx.width, cropPx.height,
    0, 0, out.width, out.height,
  );
  return new Promise((resolve) => out.toBlob(resolve, 'image/jpeg', 0.9));
}

export const SuccessStoriesCarousel = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'system_admin' || user?.role === 'team_admin';
  const canAdd = user && user.role !== 'demo';
  const [stories, setStories] = useState([]);
  const [index, setIndex] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [form, setForm] = useState({
    student_name: '', headline: '', story: '', quote: '', caption: '',
    category: 'Great Stories & Photos!',
    program: '', achievement_year: new Date().getFullYear(),
    link_url: '', link_label: '',
  });
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [existingPhotoUrl, setExistingPhotoUrl] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState(4 / 3);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [photoEdited, setPhotoEdited] = useState(false);
  const [photoEditorOpen, setPhotoEditorOpen] = useState(false);

  const resetPhotoEditor = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    setRotation(0);
    setExistingPhotoUrl(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setAspect(4 / 3);
    setCroppedAreaPixels(null);
    setPhotoEdited(false);
    setPhotoEditorOpen(false);
  };

  const canManage = (story) => isAdmin || (story?.created_by && story.created_by === user?.id);

  const emptyForm = {
    student_name: '', headline: '', story: '', quote: '', caption: '',
    category: 'Great Stories & Photos!',
    program: '', achievement_year: new Date().getFullYear(),
    link_url: '', link_label: '',
  };

  const openEdit = (story) => {
    setEditId(story.id);
    resetPhotoEditor();
    setExistingPhotoUrl(story.photo_url || null);
    setForm({
      student_name: story.student_name || '',
      headline: story.headline || '',
      story: story.story || '',
      quote: story.quote || '',
      caption: story.caption || '',
      category: story.category || 'Great Stories & Photos!',
      program: story.program || '',
      achievement_year: story.achievement_year || new Date().getFullYear(),
      link_url: story.link_url || '',
      link_label: story.link_label || '',
    });
    setAddOpen(true);
  };

  const fetchStories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/success-stories`);
      setStories(res.data || []);
      setIndex(0);
    } catch (e) {
      // silent fail — carousel hides itself if no stories
    }
  }, []);

  useEffect(() => { fetchStories(); }, [fetchStories]);

  useEffect(() => {
    const raw = sessionStorage.getItem('ss_prefill');
    if (raw && canAdd) {
      try {
        const pre = JSON.parse(raw);
        setForm((prev) => ({ ...prev, ...pre }));
        setAddOpen(true);
      } catch { /* bad payload, ignore */ }
      sessionStorage.removeItem('ss_prefill');
    }
  }, [canAdd]);

  // Auto-advance every 8 seconds when there are multiple stories
  useEffect(() => {
    if (stories.length < 2) return;
    const t = setInterval(() => setIndex(i => (i + 1) % stories.length), 8000);
    return () => clearInterval(t);
  }, [stories.length]);

  const submit = async () => {
    if (!form.student_name.trim() || !form.headline.trim() || !form.story.trim() || !form.program.trim()) {
      toast.error('Student name, title, story, and program name are required');
      return;
    }
    setSaving(true);
    try {
      let photo_url = null;
      let uploadBlob = null;
      const editorSrc = photoPreview || (photoEditorOpen && existingPhotoUrl ? mediaSrc(existingPhotoUrl) : null);
      if (editorSrc && photoEdited && croppedAreaPixels) {
        try {
          uploadBlob = await getEditedBlob(editorSrc, croppedAreaPixels, rotation);
        } catch {
          toast.warning('Could not apply the crop — saving the original photo instead');
          uploadBlob = photoFile;
        }
      } else if (photoFile) {
        uploadBlob = photoFile;
      }
      if (uploadBlob) {
        const fd = new FormData();
        fd.append('file', uploadBlob, (photoFile?.name || 'story-photo').replace(/\.[^.]+$/, '') + '.jpg');
        fd.append('user_role', user?.role || 'instructor');
        const up = await axios.post(`${API}/success-stories/upload-photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        photo_url = up.data.url;
      }
      const payload = { ...form };
      if (photo_url) payload.photo_url = photo_url;
      ['quote', 'caption'].forEach((k) => { if (!payload[k]) delete payload[k]; });
      payload.link_url = (payload.link_url || '').trim();
      payload.link_label = (payload.link_label || '').trim();
      if (!editId) {
        if (!payload.link_url) delete payload.link_url;
        if (!payload.link_label) delete payload.link_label;
      }
      if (!payload.achievement_year) delete payload.achievement_year;
      else payload.achievement_year = parseInt(payload.achievement_year, 10);
      if (editId) {
        await axios.put(`${API}/success-stories/${editId}`, payload);
      } else {
        await axios.post(`${API}/success-stories`, payload);
      }
      toast.success(editId ? 'Success story updated' : 'Success story added');
      setAddOpen(false);
      setEditId(null);
      resetPhotoEditor();
      setForm(emptyForm);
      fetchStories();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const [sheetLoading, setSheetLoading] = useState(null);

  const downloadOneSheet = async (story) => {
    setSheetLoading(story.id);
    const tId = toast.loading('Building the one-sheeter…');
    try {
      const res = await axios.get(`${API}/success-stories/${story.id}/one-sheet`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Project-LIFE-Success-Story-${(story.student_name || 'story').replace(/[^A-Za-z0-9 _-]/g, '').replace(/ /g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('One-sheeter downloaded — ready to email or share!', { id: tId });
    } catch (e) {
      toast.error('Could not generate the one-sheeter', { id: tId });
    } finally {
      setSheetLoading(null);
    }
  };

  const [emailOpen, setEmailOpen] = useState(false);
  const [emailStory, setEmailStory] = useState(null);
  const [emailTo, setEmailTo] = useState('');
  const [emailNote, setEmailNote] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  const openEmailDialog = (story) => {
    setEmailStory(story);
    setEmailTo('');
    setEmailNote('');
    setEmailOpen(true);
  };

  const sendOneSheetEmail = async () => {
    const recipients = emailTo.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean);
    if (recipients.length === 0) {
      toast.error('Enter at least one email address');
      return;
    }
    setEmailSending(true);
    try {
      const res = await axios.post(`${API}/success-stories/${emailStory.id}/email`, {
        emails: recipients,
        note: emailNote.trim() || undefined,
      });
      const { sent = [], failed = [] } = res.data || {};
      if (failed.length > 0) {
        toast.warning(`Sent to ${sent.length}, but ${failed.length} failed: ${failed.join(', ')}`);
      } else {
        toast.success(`One-sheeter emailed to ${sent.length} ${sent.length === 1 ? 'recipient' : 'recipients'}!`);
      }
      setEmailOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send the email');
    } finally {
      setEmailSending(false);
    }
  };

  const downloadPhoto = async (story) => {
    try {
      const src = story.photo_url.startsWith('http') ? story.photo_url : `${process.env.REACT_APP_BACKEND_URL}${story.photo_url}`;
      const res = await fetch(src);
      const blob = await res.blob();
      const ext = (blob.type.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${(story.student_name || 'photo').replace(/[^A-Za-z0-9 _-]/g, '').replace(/\s+/g, '-')}.${ext}`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success('Photo downloaded');
    } catch {
      toast.error('Could not download the photo');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this success story?')) return;
    try {
      await axios.delete(`${API}/success-stories/${id}`);
      toast.success('Removed');
      fetchStories();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove');
    }
  };

  // Hide the section entirely if there are no stories AND user can't add one
  if (stories.length === 0 && !canAdd) return null;

  const current = stories[index];

  return (
    <Card className="dashboard-card overflow-hidden" data-testid="success-stories-carousel">
      <CardContent className="p-0">
        {/* Header */}
        <div className="flex items-center justify-between p-5 pb-3 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5 text-[#b34e00]" fill="#e87722" />
            <h3 className="font-['Manrope'] text-xl font-bold text-[#1A1A1A]">Success Stories</h3>
            <span className="text-xs text-[#5C5C5C] ml-2">
              {stories.length > 0 ? `${index + 1} of ${stories.length}` : ''}
            </span>
          </div>
          {canAdd && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setAddOpen(true)}
              data-testid="add-success-story-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              Add story
            </Button>
          )}
        </div>

        {/* Empty state */}
        {stories.length === 0 ? (
          <div className="p-10 text-center">
            <Star className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <p className="font-['Manrope'] text-[#1A1A1A] mb-1">No success stories yet</p>
            <p className="text-sm text-[#5C5C5C]">Click &ldquo;Add story&rdquo; to celebrate a student&apos;s win.</p>
          </div>
        ) : (
          /* Carousel */
          <div className="relative">
            <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-5 items-center min-h-[200px]">
              {/* Photo */}
              <div className="sm:col-span-1 flex flex-col items-center gap-1.5">
                {current.photo_url ? (
                  <img
                    src={mediaSrc(current.photo_url)}
                    alt={current.student_name}
                    className="w-32 h-32 rounded-full object-cover border-4 border-[#e87722]/20 shadow-md"
                  />
                ) : (
                  <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#e87722] to-[#f5a261] flex items-center justify-center shadow-md">
                    <span className="text-3xl font-bold text-white">
                      {current.student_name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
                    </span>
                  </div>
                )}
                {current.caption && (
                  <p className="text-xs text-[#5C5C5C] text-center italic max-w-[10rem]">{current.caption}</p>
                )}
              </div>

              {/* Story */}
              <div className="sm:col-span-2 relative">
                <Quote className="absolute -top-2 -left-1 w-8 h-8 text-[#b34e00]/35 rotate-180" />
                <h4 className="font-['Manrope'] text-lg font-bold text-[#1A1A1A] pl-7">
                  {current.headline}
                </h4>
                <p className="text-[#5C5C5C] mt-2 pl-7 leading-relaxed">
                  {current.story}
                </p>
                {current.quote && (
                  <p className="text-[#34657f] italic mt-2 pl-7">“{current.quote}”</p>
                )}
                <div className="mt-3 pl-7 flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-semibold text-[#34657f]">— {current.student_name}</span>
                  {current.program && (
                    <span className="text-[#5C5C5C]">· {current.program}</span>
                  )}
                  {current.achievement_year && (
                    <span className="text-[#5C5C5C]">· {current.achievement_year}</span>
                  )}
                  {current.category && (
                    <Badge variant="secondary" className="text-xs bg-[#e87722]/10 text-[#b45309]">{current.category}</Badge>
                  )}
                </div>
                {current.link_url && (
                  <a
                    href={linkHref(current.link_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 mt-2 ml-7 text-sm font-semibold text-[#34657f] hover:text-[#b34e00] underline underline-offset-2 transition-colors"
                    data-testid={`story-link-${current.id}`}
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    {current.link_label || 'See more'}
                  </a>
                )}
                <div className="absolute top-0 right-0 flex items-center gap-1">
                  <button
                    onClick={() => downloadOneSheet(current)}
                    disabled={sheetLoading === current.id}
                    className="text-[#34657f] hover:text-[#2a5060] p-1.5 rounded"
                    title="Download shareable one-sheeter (PDF)"
                    aria-label="Download shareable one-sheeter PDF"
                    data-testid={`one-sheet-${current.id}`}
                  >
                    <FileDown className={`w-4 h-4 ${sheetLoading === current.id ? 'animate-pulse' : ''}`} />
                  </button>
                  {user?.role !== 'demo' && (
                    <button
                      onClick={() => openEmailDialog(current)}
                      className="text-[#34657f] hover:text-[#2a5060] p-1.5 rounded"
                      title="Email this one-sheeter"
                      aria-label="Email this one-sheeter"
                      data-testid={`email-one-sheet-${current.id}`}
                    >
                      <Mail className="w-4 h-4" />
                    </button>
                  )}
                  {isAdmin && current.photo_url && (
                    <button
                      onClick={() => downloadPhoto(current)}
                      className="text-[#34657f] hover:text-[#2a5060] p-1.5 rounded"
                      title="Download photo (for marketing/presentations)"
                      aria-label="Download photo"
                      data-testid={`download-photo-${current.id}`}
                    >
                      <ImageDown className="w-4 h-4" />
                    </button>
                  )}
                  {canManage(current) && (
                    <>
                      <button
                        onClick={() => openEdit(current)}
                        className="text-[#34657f] hover:text-[#2a5060] p-1.5 rounded"
                        title="Edit story"
                        aria-label="Edit story"
                        data-testid={`edit-story-${current.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => remove(current.id)}
                        className="text-slate-400 hover:text-red-600 p-1.5 rounded"
                        title="Remove story"
                        aria-label="Remove story"
                        data-testid={`delete-story-${current.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Nav */}
            {stories.length > 1 && (
              <>
                <button
                  onClick={() => setIndex(i => (i - 1 + stories.length) % stories.length)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white shadow border border-slate-200 flex items-center justify-center hover:bg-slate-50"
                  data-testid="carousel-prev"
                  aria-label="Previous story"
                >
                  <ChevronLeft className="w-5 h-5 text-[#34657f]" />
                </button>
                <button
                  onClick={() => setIndex(i => (i + 1) % stories.length)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white shadow border border-slate-200 flex items-center justify-center hover:bg-slate-50"
                  data-testid="carousel-next"
                  aria-label="Next story"
                >
                  <ChevronRight className="w-5 h-5 text-[#34657f]" />
                </button>
                {/* Dots */}
                <div className="flex justify-center gap-1.5 pb-4">
                  {stories.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setIndex(i)}
                      className={`h-1.5 rounded-full transition-all ${i === index ? 'w-6 bg-[#b34e00]' : 'w-1.5 bg-slate-300'}`}
                      aria-label={`Go to story ${i + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </CardContent>

      {/* Email one-sheeter dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="sm:max-w-md" data-testid="email-one-sheet-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Email this one-sheeter</DialogTitle>
            <DialogDescription>
              {emailStory ? `Sends "${emailStory.headline}" as a PDF attachment.` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="os-email-to">Recipient email(s) *</Label>
              <Input
                id="os-email-to"
                placeholder="jane@example.com, john@example.com"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                data-testid="one-sheet-email-input"
              />
              <p className="text-xs text-[#5C5C5C] mt-1">Separate multiple addresses with commas (up to 10).</p>
            </div>
            <div>
              <Label htmlFor="os-email-note">Personal note (optional)</Label>
              <Textarea
                id="os-email-note"
                rows={3}
                placeholder="e.g., Thought you'd love to see this win!"
                value={emailNote}
                onChange={(e) => setEmailNote(e.target.value)}
                maxLength={1000}
                data-testid="one-sheet-email-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)} data-testid="one-sheet-email-cancel">Cancel</Button>
            <Button onClick={sendOneSheetEmail} disabled={emailSending} className="bg-[#34657f]" data-testid="one-sheet-email-send">
              <Mail className="w-4 h-4 mr-1.5" />
              {emailSending ? 'Sending…' : 'Send email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) { setEditId(null); resetPhotoEditor(); setForm(emptyForm); } }}>
        <DialogContent className="sm:max-w-lg" data-testid="add-success-story-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">{editId ? 'Edit success story' : 'Add a success story'}</DialogTitle>
            <DialogDescription>{editId ? 'Update the details below — leave the photo empty to keep the current one.' : 'Celebrate a student win — shows up in the dashboard carousel.'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2 max-h-[65vh] overflow-y-auto pr-1">
            <div>
              <Label htmlFor="ss-name">Student name *</Label>
              <Input id="ss-name" value={form.student_name}
                onChange={e => setForm({ ...form, student_name: e.target.value })}
                data-testid="ss-student-name" />
            </div>
            <div>
              <Label htmlFor="ss-headline">Title *</Label>
              <Input id="ss-headline" placeholder="e.g., Landed a part-time job at Kroger"
                value={form.headline}
                onChange={e => setForm({ ...form, headline: e.target.value })}
                data-testid="ss-headline" />
            </div>
            <div>
              <Label htmlFor="ss-story">Story *</Label>
              <Textarea id="ss-story" rows={4} placeholder="A few sentences about the achievement…"
                value={form.story}
                onChange={e => setForm({ ...form, story: e.target.value })}
                data-testid="ss-story" />
            </div>
            <div>
              <Label htmlFor="ss-quote">Quote (optional)</Label>
              <Input id="ss-quote" placeholder="e.g., “I never thought I could do this!”"
                value={form.quote}
                onChange={e => setForm({ ...form, quote: e.target.value })}
                data-testid="ss-quote" />
            </div>
            <div>
              <Label>Category *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="ss-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GALLERY_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="ss-photo-file">Photo (optional)</Label>
              <label
                htmlFor="ss-photo-file"
                className="flex items-center gap-2 h-10 px-3 rounded-md border border-slate-300 text-sm text-[#5C5C5C] cursor-pointer hover:border-[#34657f] transition-colors"
              >
                <ImagePlus className="w-4 h-4 text-[#34657f]" />
                <span className="truncate">{photoFile ? photoFile.name : (editId ? 'Choose a new image (optional)…' : 'Choose an image…')}</span>
              </label>
              <input id="ss-photo-file" type="file" accept="image/*" className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setPhotoFile(f);
                  setRotation(0); setCrop({ x: 0, y: 0 }); setZoom(1); setPhotoEdited(false);
                  setPhotoPreview(f ? URL.createObjectURL(f) : null);
                }}
                data-testid="ss-photo-file" />
              {!photoFile && editId && existingPhotoUrl && !photoEditorOpen && (
                <div className="mt-2 flex items-center gap-3">
                  <img src={mediaSrc(existingPhotoUrl)} alt="Current story"
                    className="w-16 h-16 rounded-lg object-cover border border-slate-200" />
                  <Button type="button" variant="outline" size="sm"
                    onClick={() => setPhotoEditorOpen(true)}
                    data-testid="ss-edit-photo-btn">
                    <CropIcon className="w-4 h-4 mr-1.5" />
                    Crop or rotate current photo
                  </Button>
                </div>
              )}
              {(photoPreview || (photoEditorOpen && existingPhotoUrl)) && (
                <div className="mt-2 space-y-2" data-testid="ss-photo-editor">
                  <div className="relative w-full h-56 bg-slate-900 rounded-lg overflow-hidden">
                    <Cropper
                      image={photoPreview || mediaSrc(existingPhotoUrl)}
                      crop={crop}
                      zoom={zoom}
                      rotation={rotation}
                      aspect={aspect}
                      onCropChange={(c) => { setCrop(c); setPhotoEdited(true); }}
                      onZoomChange={(z) => { setZoom(z); setPhotoEdited(true); }}
                      onCropComplete={(_, px) => setCroppedAreaPixels(px)}
                    />
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <Button type="button" variant="outline" size="sm"
                      onClick={() => { setRotation((r) => (r + 90) % 360); setPhotoEdited(true); }}
                      data-testid="ss-rotate-photo-btn">
                      <RotateCw className="w-4 h-4 mr-1.5" />
                      Rotate
                    </Button>
                    <div className="flex items-center gap-1">
                      {[{ label: 'Landscape', v: 4 / 3 }, { label: 'Square', v: 1 }, { label: 'Portrait', v: 3 / 4 }].map((a) => (
                        <button key={a.label} type="button"
                          onClick={() => { setAspect(a.v); setPhotoEdited(true); }}
                          className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                            aspect === a.v ? 'bg-[#34657f] border-[#34657f] text-white' : 'bg-white border-slate-300 text-slate-600 hover:border-[#34657f]'
                          }`}
                          data-testid={`ss-aspect-${a.label.toLowerCase()}`}>
                          {a.label}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 flex-1 min-w-[130px]">
                      <span className="text-xs text-[#5C5C5C] shrink-0">Zoom</span>
                      <Slider value={[zoom]} min={1} max={4} step={0.05}
                        onValueChange={([z]) => { setZoom(z); setPhotoEdited(true); }}
                        aria-label="Zoom photo"
                        data-testid="ss-zoom-slider" />
                    </div>
                  </div>
                  <p className="text-xs text-[#5C5C5C]">
                    Drag to frame the student, zoom in or out, and rotate if the photo is sideways — the framed version is what gets saved.
                  </p>
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="ss-caption">Photo caption (optional)</Label>
              <Input id="ss-caption" placeholder="e.g., First day on the job!"
                value={form.caption}
                onChange={e => setForm({ ...form, caption: e.target.value })}
                data-testid="ss-caption" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ss-link-url">Link URL (optional)</Label>
                <Input id="ss-link-url" placeholder="e.g., https://local-news.com/article"
                  value={form.link_url}
                  onChange={e => setForm({ ...form, link_url: e.target.value })}
                  data-testid="ss-link-url" />
              </div>
              <div>
                <Label htmlFor="ss-link-label">Link text (optional)</Label>
                <Input id="ss-link-label" placeholder="e.g., Read the news story"
                  value={form.link_label}
                  onChange={e => setForm({ ...form, link_label: e.target.value })}
                  data-testid="ss-link-label" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ss-program">Program name *</Label>
                <Input id="ss-program" placeholder="e.g., Butler Tech Project LIFE" value={form.program}
                  onChange={e => setForm({ ...form, program: e.target.value })}
                  data-testid="ss-program" />
              </div>
              <div>
                <Label htmlFor="ss-year">Year</Label>
                <Input id="ss-year" type="number" value={form.achievement_year}
                  onChange={e => setForm({ ...form, achievement_year: e.target.value })}
                  data-testid="ss-year" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAddOpen(false); setEditId(null); resetPhotoEditor(); setForm(emptyForm); }}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#34657f]" data-testid="ss-submit">
              {saving ? 'Saving…' : (editId ? 'Save changes' : 'Save story')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default SuccessStoriesCarousel;
